import { Router, Route, Switch } from 'wouter';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SEOMeta } from './components/SEOMeta';

// Pages
import { Landing } from './pages/Landing';
import { Product } from './pages/Product';
import { Pricing } from './pages/Pricing';
import { Blog } from './pages/Blog';
import { BlogPost } from './pages/BlogPost';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { Cookies } from './pages/Cookies';
import { SignIn } from './pages/SignIn';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Switch>
          {/* Landing Page */}
          <Route path="/">
            <SEOMeta
              title="dakkio.io - Time-Series Monitoring with Natural Language"
              description="Monitor your data with plain English. Stop wrestling with complex tools. Create alerts, query data, and get insights using natural language."
              keywords="time-series monitoring, natural language interface, IoT monitoring, sensor data, data analytics, webhook notifications, embeddable widgets"
            />
            <Landing />
          </Route>

          {/* Product Page */}
          <Route path="/product">
            <SEOMeta
              title="Product Features - dakkio.io"
              description="Complete time-series monitoring platform with natural language interface, webhook notifications, embeddable widgets, and powerful APIs for developers."
              keywords="time-series database, natural language queries, IoT platform, sensor monitoring, data visualization, API integration"
            />
            <Product />
          </Route>

          {/* Pricing Page */}
          <Route path="/pricing">
            <SEOMeta
              title="Pricing - dakkio.io"
              description="Simple, transparent pricing. Start free with 100k data points/month. Scale as you grow with Pro ($29/mo), Business ($149/mo), or Enterprise plans."
              keywords="pricing, plans, free tier, subscription, data monitoring pricing"
            />
            <Pricing />
          </Route>

          {/* Blog List */}
          <Route path="/blog">
            <SEOMeta
              title="Blog - dakkio.io"
              description="Insights on time-series monitoring, natural language interfaces, and building better developer tools."
              keywords="blog, time-series, monitoring, natural language, developer tools"
            />
            <Blog />
          </Route>

          {/* Blog Post */}
          <Route path="/blog/:slug">
            <SEOMeta
              title="Introducing dakkio.io - Blog"
              description="After years of wrestling with complex monitoring tools, we built something different. Meet dakkio—time-series monitoring you can control with plain English."
              keywords="introduction, product launch, time-series monitoring, natural language"
            />
            <BlogPost />
          </Route>

          {/* Legal Pages */}
          <Route path="/terms">
            <SEOMeta
              title="Terms of Service - dakkio.io"
              description="Terms of Service for dakkio.io time-series monitoring platform."
              noindex
            />
            <Terms />
          </Route>

          <Route path="/privacy">
            <SEOMeta
              title="Privacy Policy - dakkio.io"
              description="Privacy Policy for dakkio.io. Learn how we collect, use, and protect your data."
              noindex
            />
            <Privacy />
          </Route>

          <Route path="/cookies">
            <SEOMeta
              title="Cookie Policy - dakkio.io"
              description="Cookie Policy for dakkio.io. Learn about the cookies we use and how to control them."
              noindex
            />
            <Cookies />
          </Route>

          {/* Auth Pages */}
          <Route path="/signup">
            <SEOMeta
              title="Get Started - dakkio.io"
              description="Request early access to dakkio.io. We're onboarding select companies to our time-series monitoring platform with natural language interface."
              keywords="early access, sign up, time-series monitoring, waitlist"
            />
            <SignIn />
          </Route>

          <Route path="/signin">
            <SEOMeta
              title="Sign In - dakkio.io"
              description="Sign in to your dakkio.io dashboard or request early access to our time-series monitoring platform."
              keywords="sign in, login, dashboard, time-series monitoring"
            />
            <SignIn />
          </Route>

          {/* 404 */}
          <Route>
            <SEOMeta
              title="Page Not Found - dakkio.io"
              description="The page you're looking for doesn't exist."
              noindex
            />
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-slate-900 mb-4">404</h1>
                <p className="text-xl text-slate-600 mb-8">Page not found</p>
                <a
                  href="/"
                  className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors inline-block"
                >
                  Go Home
                </a>
              </div>
            </div>
          </Route>
        </Switch>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
